import { computed, inject, Signal, signal, untracked } from '@angular/core';
import { LogsData } from '@shared/services/logs';
import { isEmptyObject } from '@shared/utils';
import { BatchUpdatePayload } from '@stores/abstract/models';

/**
 * Abstract base class for reactive state management using Angular signals.
 *
 * @template T - State object type
 *
 * @remarks
 * - Override `storeName` for meaningful logging
 * - State is immutable; updates create new state objects
 * - All changes are automatically logged
 *
 * @example
 * ```
 * interface UserState {
 *   name: string;
 *   email: string;
 *   role: 'admin' | 'user';
 * }
 *
 * class UserStore extends StoreBase<UserState> {
 *   override storeName = 'UserStore';
 *
 *   constructor() {
 *     super();
 *     this.set({ name: '', email: '', role: 'user' });
 *   }
 * }
 *
 * // In a component
 * export class UserProfileComponent {
 *   private userStore = inject(UserStore);
 *
 *   userName = this.userStore.select('name');
 *
 *   updateUserName(newName: string): void {
 *     this.userStore.update('name', newName);
 *   }
 * }
 * ```
 */
export abstract class StoreBase<T> {
  /**
   * Identifier for logging state changes (override in subclasses).
   */
  storeName: string = 'Store';

  private readonly store = signal<T | null>(null);

  /**
   * Readonly signal providing reactive access to the entire state.
   */
  read = this.store.asReadonly();

  private readonly logs = inject(LogsData);

  /**
   * Replaces the entire store state.
   *
   * @param data - New complete state object
   *
   * @remarks
   * Prefer `update()` for partial state updates.
   *
   * @example
   * ```
   * this.set({ products: [], loading: false, error: null });
   * ```
   */
  set(data: T): void {
    this.store.set(data);
    if (this.storeName === 'Store') {
      this.logs.warn('💽 StoreBase', 'Store name is not defined', '\nState changed', data);
    } else {
      this.logs.info(`💽 ${this.storeName}`, 'State changed', data);
    }
  }

  /**
   * Creates a computed signal tracking a specific state property.
   *
   * @param key - Property key to select
   * @returns Signal containing the property value or undefined
   *
   * @example
   * ```
   * items = this.cartStore.select('items');
   * total = this.cartStore.select('total');
   * ```
   */
  select<K extends keyof T>(key: K): Signal<NonNullable<T>[K] | undefined> {
    return computed(() => this.store()?.[key]);
  }

  /**
   * Creates a computed signal tracking a derived value from the state.
   *
   * @param selector - Function extracting or computing a value from state
   * @returns Signal containing the selected/computed value or undefined
   *
   * @example
   * ```
   * // Select nested property
   * customerName = this.orderStore.selectPath(state => state.customer.name);
   *
   * // Compute derived value
   * totalWithTax = this.orderStore.selectPath(state =>
   *   state.subtotal + (state.subtotal * state.taxRate)
   * );
   * ```
   */
  selectPath<R>(selector: (state: T | null) => R): Signal<R | undefined> {
    return computed(() => {
      const state = this.store();
      return isEmptyObject(state) ? undefined : selector(state);
    });
  }

  /**
   * Updates a single state property while preserving others.
   *
   * @param key - Property key to update
   * @param value - New property value
   *
   * @example
   * ```
   * this.update('loading', true);
   * this.update('user', userData);
   * ```
   */
  update<K extends keyof T>(key: K, value: T[K]): void {
    let currentState = untracked(this.store);
    if (!currentState) currentState = {} as T;
    this.set({ ...currentState, [key]: value });
  }

  /**
   * Removes a property from the state.
   *
   * @param key - Property key to remove
   *
   * @remarks
   * Prefer nullable properties over deletion.
   */
  delete<K extends keyof T>(key: K): void {
    const currentState = untracked(this.store);
    if (currentState) {
      const newState = { ...currentState };
      delete newState[key];
      this.set(newState as T);
    }
  }

  /**
   * Performs multiple updates and/or deletions in a single atomic operation.
   *
   * @param payload - Update and delete operations
   * @param payload.update - Properties to update or add
   * @param payload.delete - Property keys to remove
   *
   * @remarks
   * More efficient than chaining multiple update/delete calls.
   *
   * @example
   * ```
   * this.batchUpdate({
   *   update: {
   *     loading: false,
   *     submitted: true
   *   },
   *   delete: ['error', 'validationErrors']
   * });
   * ```
   */
  batchUpdate(payload: BatchUpdatePayload<T>): void {
    let currentState = untracked(this.store);
    if (!currentState) currentState = {} as T;

    let newState = { ...currentState };

    if (payload.update) {
      newState = { ...newState, ...payload.update };
    }

    if (payload.delete) {
      for (const key of payload.delete) {
        delete newState[key];
      }
    }
    this.set(newState);
  }
}
